import React from "react";
import { DotLottieReact } from "@lottiefiles/dotlottie-react";
import type { DownloadPhase } from "../DownloadData";

interface Props {
  phase: DownloadPhase;
  statusMessage: string;
  onDownload: () => void;
  onDismissError: () => void;
  onCancel: () => void;
}

export function DownloadButton({
  phase,
  statusMessage,
  onDownload,
  onDismissError,
  onCancel,
}: Props) {
  const isLoading = phase === "submitting" || phase === "polling";
  const isSuccess = phase === "success";
  const isError = phase === "error";

  return (
    <div
      className="dd-download-cta-row"
      style={{ flexDirection: "column", alignItems: "center", gap: 12 }}
    >
      {/* Status banner */}
      {(isLoading || isSuccess || isError) && (
        <div className={`dd-status-banner dd-status-banner--${phase}`}>
          {isLoading && (
            <DotLottieReact
              src="https://lottie.host/e5d43299-8a12-4fd2-9cd9-17750f9f56c5/lZjt4YNRaX.lottie"
              loop
              autoplay
              style={{ width: 32, height: 32, flexShrink: 0 }}
            />
          )}
          {isSuccess && (
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              style={{ flexShrink: 0 }}
            >
              <circle cx="10" cy="10" r="10" fill="#22c55e" />
              <polyline
                points="5,10.5 8.5,14 15,7"
                stroke="#fff"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          )}
          {isError && (
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              style={{ flexShrink: 0 }}
            >
              <circle cx="10" cy="10" r="10" fill="#ef4444" />
              <line
                x1="6"
                y1="6"
                x2="14"
                y2="14"
                stroke="#fff"
                strokeWidth="2"
                strokeLinecap="round"
              />
              <line
                x1="14"
                y1="6"
                x2="6"
                y2="14"
                stroke="#fff"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          )}
          <span className="dd-status-banner__text">{statusMessage}</span>
          {isError && (
            <button
              className="dd-status-banner__dismiss"
              onClick={onDismissError}
              aria-label="Dismiss"
            >
              ✕
            </button>
          )}
        </div>
      )}

      {/* Download + Cancel buttons side by side */}
      <div style={{ display: "flex", gap: 8 }}>
        <button
          type="button"
          className={`dd-download-btn${isLoading ? " dd-download-btn--loading" : ""}${isSuccess ? " dd-download-btn--success" : ""}`}
          onClick={onDownload}
          disabled={isLoading || isSuccess}
          id="gridco-download-data-btn"
        >
          {isLoading ? (
            <span className="dd-download-btn__inner">
              <span className="dd-download-spinner" />
              {phase === "submitting" ? "Submitting…" : "Processing…"}
            </span>
          ) : isSuccess ? (
            <span className="dd-download-btn__inner">
              <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                <polyline
                  points="4,10 8,14 16,6"
                  stroke="#fff"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Downloaded!
            </span>
          ) : (
            <span className="dd-download-btn__inner">
              <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                <path
                  d="M10 3v10M10 13l-4-4M10 13l4-4"
                  stroke="#fff"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M3 17h14"
                  stroke="#fff"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
              Download
            </span>
          )}
        </button>

        {isLoading && (
          <button type="button" className="dd-cancel-btn" onClick={onCancel}>
            Cancel
          </button>
        )}
      </div>
    </div>
  );
}
